import { createUseStyles } from 'react-jss';
import { WrapWithCardOnLargerScreen } from '../components/WrapWithCardOnLargerScreen';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { InnerhivePage } from '../InnerhivePage';
import { useEffect, useState } from 'react';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { HomePageMenu } from './HomePageMenu';
import { useSubscription } from '@meraki-internal/state';
import { Onboarding } from '../onboarding/Onboarding';
import { MDTabs } from '../components/MDTabs';
import { OrganizationClients } from '../organizations/OrganizationClients';
import { OrganizationTeam } from '../organizations/OrganizationTeam';
import { OrganizationsState } from '../organizations/OrganizationsState';
import { HomePageBanner } from './HomePageBanner';
import { CaregiverTools } from './CaregiverTools';
import { CareMapState } from '../caremap/CareMapState';
import { ActiveCareMapProviderV2 } from '../ActiveCareMapProvider';
import { initCap } from '../utils/format';
import { CareRecipientSwitcher } from '../navigation/CareRecipientSwitcher';
import { HeaderTabsState } from '../HeaderTabs';
import { Recordings } from './Recordings';
import { GetHelpPromptViewModel } from '../get-help/GetHelpPromptViewModel';

const useStyles = createUseStyles({
    page: {
        width: '100%'
    },
    content: {
        position: 'relative',
        width: '100%',
        height: '100%',
        maxWidth: 650,
        margin: 'auto'
    }
});

type ITabs = 'recordings' | 'caregiver' | 'onboarding';

export const CaregiverHomePage: React.FC<{ careMapId?: string;}> = ({ careMapId }) => {
    const classes = useStyles();
    const mixpanel = useInstance(MixPanelEventEmitter);

    const orgsState = useInstance(OrganizationsState);
    useSubscription(() => orgsState);

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const tabsState = useInstance(HeaderTabsState);
    useSubscription(() => tabsState);

    const getHelpPromptVM = useInstance(GetHelpPromptViewModel);
    useSubscription(() => getHelpPromptVM);

    const defaultSelection = 'recordings';
    const [selectedTab, setSelectedTab] = useState<ITabs>(defaultSelection);

    const tabGroupId = 'home-page-tabs';

    const trackTab = (tab: ITabs) => {
        mixpanel.track(`${tab === 'recordings' ? 'Recordings' : tab === 'caregiver' ? 'Caregiver Support' : 'Build Your Innerhive'} Tab Opened`);
    };

    useEffect(() => {
        mixpanel.track(`Caregiver's Corner Opened`);

        const selectedDataId: ITabs = tabsState.getLastActiveTab(tabGroupId)?.dataId || defaultSelection;
        setSelectedTab(selectedDataId);
        trackTab(selectedDataId);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (selectedTab) {
            tabsState.setLastActiveTab(tabGroupId, {label: '', url: '', dataId: selectedTab});
        }
    }, [selectedTab]); // eslint-disable-line react-hooks/exhaustive-deps

    // active caremap is needed for both onboarding and checkins
    const activeCareMapId = careMapId || careMapState.getMostRecentCaremap()?.careMapId;

    const onChangeTab = (tab: typeof selectedTab) => {
        setSelectedTab(tab);
        trackTab(tab);
    };

    const orgId = (orgsState.isOrgTeamMember() ? orgsState.getMostRecentOrganization()?.orgId : undefined);
    const [orgTab, setOrgTab] = useState<'clients' | 'team' | 'tools'>('clients');
    const onChangeOrgTab = (tab: typeof orgTab) => {
        setOrgTab(tab);
        mixpanel.track(`${initCap(tab)} Tab Opened`);
    };

    return (
        <ActiveCareMapProviderV2 careMapId={activeCareMapId}>

            <InnerhivePage hideAppHeader backgroundColor="light" skipRequireName>
                <div className={classes.page}>

                    <HomePageMenu />
                    <HomePageBanner />

                    <div className={classes.content}>
                        <WrapWithCardOnLargerScreen marginTop={-30} marginBottom={30}>
                            <div style={{minHeight: 400, padding: 10}}>

                                {!orgId ? (
                                    <>
                                        <MDTabs
                                            fullWidth
                                            value={selectedTab}
                                            onChange={onChangeTab}
                                            buttons={[
                                                { label: 'Record', value: 'recordings' },
                                                { label: 'Connect', value: 'caregiver' },
                                                { label: 'Setup', value: 'onboarding' }
                                            ]}
                                        />

                                        {selectedTab === 'recordings' && (
                                            <div  data-id="home-recording-list" style={{paddingTop: 10, marginBottom: 20}}>
                                                <Recordings />
                                            </div>
                                        )}

                                        {selectedTab === 'caregiver' && (
                                            <div style={{paddingTop: 10, marginBottom: 20}}>
                                                <CaregiverTools />
                                            </div>
                                        )}

                                        {selectedTab === 'onboarding' && (
                                            <div style={{paddingTop: 10, marginBottom: 20}}>
                                                <Onboarding />
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <MDTabs
                                            value={orgTab}
                                            onChange={onChangeOrgTab}
                                            buttons={[
                                                { label: 'Clients', value: 'clients' },
                                                { label: 'Team', value: 'team' },
                                                { label: 'Tools', value: 'tools' }
                                            ]}
                                        />

                                        <div style={{paddingTop: 10}}>
                                            {orgTab === 'clients' && orgId &&
                                                <OrganizationClients orgId={orgId} />
                                            }
                                            {orgTab === 'team' && orgId &&
                                                <OrganizationTeam orgId={orgId} />
                                            }
                                            {orgTab === 'tools' &&
                                                <>
                                                    <div style={{padding: 10}}>
                                                        <CareRecipientSwitcher
                                                            onClick={() => setOrgTab('clients')}
                                                        />
                                                    </div>
                                                    <CaregiverTools />
                                                </>
                                            }
                                        </div>
                                    </>
                                )}

                            </div>
                        </WrapWithCardOnLargerScreen>
                    </div>

                </div>
            </InnerhivePage>

        </ActiveCareMapProviderV2>
    );
};
